import { Grid } from "@mui/material";
import { FormikProps } from "formik";
import Flex from "@/components/Flex";
import Input from "@/components/Input";
import Text from "@/components/Text";
import { getAge } from "@/utils/utils";
import {
  AUTHORIZATION_EXPIRY,
  CCM_CONSENT_STYLE,
  CCM_CONSENT_TEXT,
  EMERGENCY_CONTACT_FIELDS,
  INSURANCE_FIELDS,
  PATIENT_DETAIL_FIELDS,
  PATIENT_FIELDS,
  REQUIRED_MARK,
} from "../ccm-consent-constant";
import {
  EXPIRY_FIELD_NAME,
  GENDER_OPTIONS,
  PREFILLED_PATIENT_FIELD_NAMES,
  RELEASE_ITEM_OPTIONS,
  CCM_DEFAULT_EXPIRY_MONTHS,
} from "../ccm-consent-copy-constant";
import {
  AuthorizationExpiry,
  CcmConsentFormValues,
  PracticeContact,
  ReleaseItem,
} from "../ccm-consent-types";
import {
  ConsentCheckbox,
  ConsentField,
  ConsentFieldGrid,
  ConsentSection,
  getConsentFieldError,
} from "./ConsentFormFields";
import ConsentExpiryNote from "./ConsentExpiryNote";
import {
  getConsentSignatureError,
  toDefaultExpiryDate,
  lockConsentFields,
} from "../ccm-consent-utils";

const LOCKED_PATIENT_FIELDS = lockConsentFields(
  PATIENT_FIELDS,
  PREFILLED_PATIENT_FIELD_NAMES
);
const LOCKED_DETAIL_FIELDS = lockConsentFields(
  PATIENT_DETAIL_FIELDS,
  PREFILLED_PATIENT_FIELD_NAMES
);
const LOCKED_EMERGENCY_CONTACT_FIELDS = lockConsentFields(
  EMERGENCY_CONTACT_FIELDS,
  PREFILLED_PATIENT_FIELD_NAMES
);

export interface CcmPatientSectionsProps {
  formik: FormikProps<CcmConsentFormValues>;
  practiceContact: PracticeContact;
}

const CcmPatientSections = ({
  formik,
  practiceContact,
}: CcmPatientSectionsProps) => {
  const { patient, releaseAuthorization } = formik.values;
  const { releaseItems, expiry } = releaseAuthorization;
  const genderError = getConsentFieldError(formik, "patient.gender");

  const selectGender = (gender: typeof patient.gender) => {
    formik.setFieldTouched("patient.gender", true, false);
    formik.setFieldValue("patient.gender", gender);
  };

  const toggleReleaseItem = (item: ReleaseItem, isChecked: boolean) =>
    formik.setFieldValue(
      "releaseAuthorization.releaseItems",
      isChecked
        ? [...releaseItems, item]
        : releaseItems.filter((selected) => selected !== item)
    );

  const selectExpiry = (value: AuthorizationExpiry, isChecked: boolean) => {
    formik.setFieldValue("releaseAuthorization.expiry", isChecked ? value : "");
    formik.setFieldValue(
      "releaseAuthorization.expiryDate",
      isChecked && value === AUTHORIZATION_EXPIRY.DATE
        ? toDefaultExpiryDate(CCM_DEFAULT_EXPIRY_MONTHS)
        : null
    );
  };

  return (
    <>
      <ConsentSection title={CCM_CONSENT_TEXT.PATIENT_INFORMATION}>
        <Grid container spacing={2} alignItems="center">
          {LOCKED_PATIENT_FIELDS.map((field) => (
            <Grid key={field.name} item xs={12} sm={field.sm}>
              <ConsentField formik={formik} field={field} />
            </Grid>
          ))}
          <Grid item xs={12} sm={2}>
            <Input
              label={CCM_CONSENT_TEXT.AGE}
              clearable={false}
              textFieldProps={{
                value: patient.dateOfBirth ? getAge(patient.dateOfBirth) : "",
                inputProps: { readOnly: true },
                fullWidth: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Flex alignItems="center" columnGap={2} flexWrap="wrap">
              <Text>
                {CCM_CONSENT_TEXT.GENDER}
                <Text component="span" color="error">
                  {REQUIRED_MARK}
                </Text>
              </Text>
              {GENDER_OPTIONS.map((option) => (
                <ConsentCheckbox
                  key={option.value}
                  label={option.label}
                  isDisabled
                  checked={patient.gender === option.value}
                  onChange={(isChecked) =>
                    selectGender(isChecked ? option.value : "")
                  }
                />
              ))}
            </Flex>
            {genderError && (
              <Text color="error" variant="caption">
                {genderError}
              </Text>
            )}
          </Grid>
        </Grid>
        <ConsentFieldGrid formik={formik} fields={LOCKED_DETAIL_FIELDS} />
      </ConsentSection>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6} display="flex">
          <ConsentSection
            title={CCM_CONSENT_TEXT.INSURANCE_INFORMATION}
            sx={{ flex: 1 }}
          >
            <Text
              fontWeight={CCM_CONSENT_STYLE.HEADING_WEIGHT}
              color={CCM_CONSENT_STYLE.SUB_HEADING}
            >
              {CCM_CONSENT_TEXT.INSURANCE_TYPE}
            </Text>
            <ConsentFieldGrid formik={formik} fields={INSURANCE_FIELDS} />
          </ConsentSection>
        </Grid>
        <Grid item xs={12} md={6} display="flex">
          <ConsentSection
            title={CCM_CONSENT_TEXT.EMERGENCY_CONTACT}
            sx={{ flex: 1 }}
          >
            <ConsentFieldGrid
              formik={formik}
              fields={LOCKED_EMERGENCY_CONTACT_FIELDS}
            />
          </ConsentSection>
        </Grid>
      </Grid>

      <ConsentSection title={CCM_CONSENT_TEXT.RELEASE_AUTHORIZATION}>
        <Text textAlign="center" color={CCM_CONSENT_STYLE.SUB_HEADING}>
          {[
            practiceContact.name,
            `${CCM_CONSENT_TEXT.PHONE} ${practiceContact.phone}`,
            `${CCM_CONSENT_TEXT.FAX} ${practiceContact.fax}`,
            `${CCM_CONSENT_TEXT.EMAIL} ${practiceContact.email}`,
          ].join(" | ")}
        </Text>
        <Text>{CCM_CONSENT_TEXT.RELEASE_FOLLOWING}</Text>
        <Flex flexWrap="wrap" columnGap={1}>
          {RELEASE_ITEM_OPTIONS.map((option) => (
            <ConsentCheckbox
              key={option.value}
              label={option.label}
              checked={releaseItems.includes(option.value)}
              onChange={(isChecked) =>
                toggleReleaseItem(option.value, isChecked)
              }
            />
          ))}
        </Flex>
        <Text textAlign="center">{CCM_CONSENT_TEXT.AUTHORIZATION_EXPIRES}</Text>
        <Flex justifyContent="center">
          <Flex alignItems="center" flexWrap="wrap" gap={2} position="relative">
            <ConsentCheckbox
              label={CCM_CONSENT_TEXT.NEVER_EXPIRES}
              checked={expiry === AUTHORIZATION_EXPIRY.NEVER_EXPIRES}
              onChange={(isChecked) =>
                selectExpiry(AUTHORIZATION_EXPIRY.NEVER_EXPIRES, isChecked)
              }
            />
            <ConsentCheckbox
              label={CCM_CONSENT_TEXT.DATE}
              checked={expiry === AUTHORIZATION_EXPIRY.DATE}
              onChange={(isChecked) =>
                selectExpiry(AUTHORIZATION_EXPIRY.DATE, isChecked)
              }
            />
            {/* Out of flow so the centred checkboxes don't move when it shows. */}
            <Text
              variant="caption"
              color="error"
              position="absolute"
              left="100%"
              ml={2}
              whiteSpace="nowrap"
            >
              {getConsentSignatureError(formik, EXPIRY_FIELD_NAME)}
            </Text>
          </Flex>
        </Flex>
        <ConsentExpiryNote
          expiry={expiry}
          expiryDate={releaseAuthorization.expiryDate}
          defaultMonths={CCM_DEFAULT_EXPIRY_MONTHS}
        />
      </ConsentSection>
    </>
  );
};

export default CcmPatientSections;
